import React from 'react';

import { Close } from '@mui/icons-material';
import { OverlayDisplayDiv } from '@UI-Elements/PopoverOverlays/OverlayDisplayDiv';
import { CocoaExposureChart } from './CocoaDash/CocoaExposureChart';
import { ExposureCard } from '../ExposureCard';
import {
  getEntriesFiltered,
  getExposureCardData,
  getFormulaToAccountById,
  invReportsToStackedBar,
  reportsToSummaryMap,
} from '../dashboardUtils';
import { useAccountStore } from '../../../components/Stores/accountStore';
import { useMaterialsStore } from '../../../components/Stores/MaterialsStore';
import { useExposureStore } from '../../../components/Stores/exposureDataStore';
import { useCurrencyRates } from '@hooks/useCurrencyRates';
import { IInventoryReportType } from '@sp-types/InventoryReportType';
import { StackedBarChartElement } from '@sp-types/dashboardTypes';

interface Props {
  close: () => void;
  formulaId: string;
  orders: StackedBarChartElement[];
  inventory: IInventoryReportType[];
  positions: StackedBarChartElement[];
}
export const ExposureDetailsPopup: React.FC<Props> = ({ close, formulaId, orders, inventory, positions }) => {
  const { account, formulaColorDictionary: colorsMap } = useAccountStore();
  const { materialsMap } = useMaterialsStore();
  const { pricesMap } = useCurrencyRates();
  const { netExposure, isNetExposureLoading } = useExposureStore();
  const { convertionMap } = useCurrencyRates();

  const [inventoryValues, setInventoryValues] = React.useState<number[]>([]);
  const [ordersValues, setOrdersValues] = React.useState<number[]>([]);
  const [positionsValues, setPositionsValues] = React.useState<number[]>([]);

  React.useEffect(() => {
    if (inventory) {
      const materialReportsMap = reportsToSummaryMap(inventory, 0, materialsMap, pricesMap, false);
      const materialsEntries = getEntriesFiltered(materialReportsMap, formulaId);
      const barChartData = invReportsToStackedBar(materialsEntries, materialsMap, colorsMap);

      setInventoryValues(
        barChartData.find((a) => a.formulaId === formulaId)?.valueArray.map(({ value }) => value) || [],
      );
    }

    if (orders) {
      const flatOrders = [
        0,
        ...orders.filter((o) => o.formulaId === formulaId).flatMap((order) => order.valueArray.map((a) => a.value)),
      ];

      setOrdersValues(flatOrders);
    }

    if (positions) {
      setPositionsValues([
        0,
        ...positions
          .filter((p) => p.formulaId === formulaId)
          .flatMap((position) => position.valueArray.map((a) => a.value)),
      ]);
    }
  }, [inventory, orders, formulaId, materialsMap, colorsMap]);

  return (
    <OverlayDisplayDiv setDisplayOverlay={close} hideClose>
      <div className="bg-white flex flex-col p-4 gap-4 min-w-96">
        <div className="flex flex-row justify-between">
          <h4>
            Detailed exposure of
            <span style={{ color: colorsMap[formulaId] || '#000' }}> {materialsMap[formulaId]?.name}</span>
          </h4>
          <Close className="cursor-pointer align-self-baseline text-purple me-1" onClick={close} />
        </div>
        <div className="flex">
          <ExposureCard
            {...getExposureCardData(
              netExposure,
              getFormulaToAccountById(account, formulaId || 'general'),
              account.riskPercents,
              convertionMap,
              materialsMap,
              materialsMap[formulaId],
            )}
            material={materialsMap[formulaId]}
            isMiniView
          />
        </div>
        <div className="gap-2 flex flex-col">
          <div className="flex flex-col  shadow-custom p-3 gap-3 w-[50rem] h-[30rem]">
            <CocoaExposureChart
              material={formulaId === 'RFVCLLEUDC1' ? 'mass' : formulaId === 'RFVCBLEUDC1' ? 'butter' : 'other'}
              orders={ordersValues}
              positions={positionsValues}
              inventory={inventoryValues}
              hasRatio={!!materialsMap[formulaId]?.hasRatio}
            />
          </div>
        </div>
      </div>
    </OverlayDisplayDiv>
  );
};
